<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  agents: { type: Array, default: () => [] },
  isLoading: { type: Boolean, default: false },
});

const emit = defineEmits(['select-agent']);
const { t } = useI18n();

const headers = computed(() => [
  t('PDV_SALES_REPORT.AGENT_TABLE.POSITION'),
  t('PDV_SALES_REPORT.AGENT_TABLE.AGENT'),
  t('PDV_SALES_REPORT.AGENT_TABLE.SALES'),
  t('PDV_SALES_REPORT.AGENT_TABLE.REVENUE'),
  t('PDV_SALES_REPORT.AGENT_TABLE.AVG_TICKET'),
  t('PDV_SALES_REPORT.AGENT_TABLE.AVG_DISCOUNT'),
  t('PDV_SALES_REPORT.AGENT_TABLE.TOP_PAYMENT'),
]);

const formatCurrency = val =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val || 0);
</script>

<template>
  <div class="overflow-x-auto">
    <div v-if="isLoading" class="flex items-center justify-center py-8 text-slate-500">
      <span class="i-lucide-loader-circle animate-spin mr-2" />
    </div>
    <table v-else class="w-full text-sm text-left">
      <thead>
        <tr class="border-b border-slate-100 dark:border-slate-700">
          <th
            v-for="header in headers"
            :key="header"
            class="py-2 px-3 font-medium text-slate-500 dark:text-slate-400"
          >
            {{ header }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(agent, index) in agents"
          :key="agent.agent_id"
          class="border-b border-slate-50 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer"
          @click="emit('select-agent', agent)"
        >
          <td class="py-2 px-3 font-medium text-slate-400 dark:text-slate-500 w-8">
            {{ index + 1 }}
          </td>
          <td class="py-2 px-3 font-medium text-slate-800 dark:text-slate-100">
            {{ agent.agent_name }}
          </td>
          <td class="py-2 px-3 text-slate-600 dark:text-slate-300">{{ agent.sales_count }}</td>
          <td class="py-2 px-3 text-slate-600 dark:text-slate-300">
            {{ formatCurrency(agent.total_revenue) }}
          </td>
          <td class="py-2 px-3 text-slate-600 dark:text-slate-300">
            {{ formatCurrency(agent.avg_ticket) }}
          </td>
          <td class="py-2 px-3 text-slate-600 dark:text-slate-300">
            {{ formatCurrency(agent.avg_discount) }}
          </td>
          <td class="py-2 px-3 text-slate-600 dark:text-slate-300">
            {{ agent.top_payment_method || '—' }}
          </td>
        </tr>
        <tr v-if="!agents.length">
          <td colspan="7" class="py-8 text-center text-slate-400">
            {{ $t('PDV_SALES_REPORT.NO_DATA') }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
